import Backbone from 'backbone';
import * as d3 from 'd3';

import {PolymerAlignmentTabState} from 'analyses/alignment/PolymerAlignmentTabView';
// @ts-expect-error
import SummarySliceLineView from 'analyses/alignment/SummarySliceLineView';
// @ts-expect-error
import SummarySliceView from 'analyses/alignment/SummarySliceView';
import SummaryMapTemplate from 'analyses/SummaryMapTemplate';
import Slice from 'molbio/maps/linear/Slice';
import {SummaryMapState} from 'molbio/maps/MapStates';
import ProteinAlignmentTabState from 'molbio/proteins/alignments/ProteinAlignmentTabState';
import AlignmentTabState from 'molbio/tabstates/AlignmentTabState';
import {Optional} from 'util/Types';

// @ts-expect-error: The model is null for proteins
export default class AlignmentSummaryMapView extends Backbone.View<SummaryMapState | null> {
  tagName!: string;
  className!: string;
  template!: typeof SummaryMapTemplate;
  model!: SummaryMapState | null;
  tabstate!: PolymerAlignmentTabState; // AlignmentTabState or ProteinAlignmentTabState;
  isDragging!: boolean;
  slice!: Slice;
  padding!: number;
  isCollapsed!: boolean;
  topSliceView!: SummarySliceView;
  sliceView!: SummarySliceLineView;
  bottomSliceView!: SummarySliceLineView;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'summary-map';
    this.prototype.template = SummaryMapTemplate;
  }

  initialize(
    // @ts-expect-error: The model is null for proteins
    options: Backbone.ViewOptions<SummaryMapState | null> & {
      model: SummaryMapState | null;
      tabstate: AlignmentTabState | ProteinAlignmentTabState;
      padding: Optional<number>;
      isCollapsed: Optional<boolean>;
      topY: Optional<Array<string>>;
      middleY: Optional<Array<string>>;
      bottomY: Optional<Array<string>>;
    }
  ): void {
    super.initialize(options);

    this.model = options.model;
    this.tabstate = options.tabstate;
    this.isDragging = false;
    this.slice = new Slice();
    this.padding = options.padding || 0;
    this.isCollapsed = options.isCollapsed || false;

    this.topSliceView = new SummarySliceView({
      slice: this.slice,
      model: this.model,
      tabstate: this.tabstate,
      mapType: 'summary',
      top: true,
      marginBottom: 5,
      y: options.topY || (this.isCollapsed ? ['line'] : ['alignmentComments', 'line']),
    });
    if (!this.isCollapsed) {
      this.sliceView = new SummarySliceLineView({
        slice: this.slice,
        model: this.model,
        tabstate: this.tabstate,
        top: false,
        mapType: 'summary',
        y: options.middleY || ['line'],
      });
      this.bottomSliceView = new SummarySliceView({
        slice: this.slice,
        model: this.model,
        tabstate: this.tabstate,
        top: false,
        mapType: 'summary',
        y: options.bottomY || ['sequenceAxis'],
      });
    }

    this.showFullSequence();
  }

  showFullSequence(): void {
    const width = this.tabstate.getPolymerLength();
    this.tabstate.fitSummaryTypeToWidth(width, this.mapWidth(), {allowFractionalSpacing: true});
    this.setSliceValues();
    this.setSVGWidth();
  }

  setSVGWidth(): void {
    const width = this.tabstate.getSummaryType().width(this.tabstate.getPolymerLength());
    const sliceViews =
      Boolean(this.sliceView) && Boolean(this.bottomSliceView)
        ? [this.topSliceView, this.sliceView, this.bottomSliceView]
        : [this.topSliceView];
    for (const sliceView of sliceViews) {
      sliceView.setWidth(width + 1);
    }
  }

  mapWidth(): number {
    return (
      this.tabstate.get('bufferViewLayout')!.get('width') -
      30 -
      this.padding +
      (this.isCollapsed ? this.padding : 0)
    );
  }

  setSliceValues(): void {
    const start = 0;
    const end = this.tabstate.getPolymerLength();
    const width = this.tabstate.getSummaryType().width(end - start);
    const x = d3.scaleLinear().clamp(true).domain([start, end]).range([0, width]);
    const xInv = d3.scaleLinear().clamp(true).domain([0, width]).range([start, end]);
    this.slice.set({start, end, width, x, xInv, maxWidth: width, visibleWidth: this.mapWidth()});
  }

  killView(view: SummarySliceView | SummarySliceLineView): void {
    if (view) {
      view.die();
    }
  }

  stopListening(): void {
    super.stopListening();
    this.killView(this.topSliceView);
    this.killView(this.sliceView);
    this.killView(this.bottomSliceView);
  }

  resizeHandler(): void {
    this.showFullSequence();
  }

  render(): AlignmentSummaryMapView {
    this.$el.html(this.template());

    const attachSliceView = (
      sliceView: SummarySliceView | SummarySliceLineView,
      containerClass: string
    ): SummarySliceView | SummarySliceLineView => {
      const svg = d3
        .select(this.el)
        .select(containerClass)
        .append('svg:svg')
        .attr('xmlns', 'http://www.w3.org/2000/svg')
        .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
        .classed('mini-map', true); // for styling purposes.
      const el = svg.node();
      sliceView.setElement(el);
      svg.classed(sliceView.className, true);
      return sliceView.render();
    };
    attachSliceView(this.topSliceView, '.top-map');
    if (this.sliceView) {
      attachSliceView(this.sliceView, '.map');
      this.sliceView.showVerticalSlice(0, 200); // max-height constant
    }
    if (this.bottomSliceView) {
      attachSliceView(this.bottomSliceView, '.bottom-map');
    }

    this.$('.map-wrap').on('scroll', () => {
      const top = this.$('.map-wrap').scrollTop();
      const bottom = top + this.$('.map-wrap').height();
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      return this.sliceView.showVerticalSlice(top, bottom);
    });

    this.setSVGWidth();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.tabstate.get('bufferViewLayout'), 'change:width', this.resizeHandler);
    return this;
  }
}
AlignmentSummaryMapView.initClass();
